import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Drawer,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import apiClient from '../../../lib/api';

const BranchDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  // Handle both routes: /branches/:id and /companies/:id/branches/:id
  const branchId = params.id; // Branch ID (works for both routes)
  const companyIdFromRoute = params.companyId; // Company ID from nested route (if present)
  const id = branchId; // Keep for backward compatibility
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [companyId, setCompanyId] = useState(companyIdFromRoute || '');
  const [formData, setFormData] = useState({
    branchName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    contactPersonName: '',
    description: '',
    type: '',
  });
  const [originalData, setOriginalData] = useState({
    branchName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    contactPersonName: '',
    description: '',
    type: '',
  });
  const [errors, setErrors] = useState({});
  const [branch, setBranch] = useState(null);

  useEffect(() => {
    if (id && id !== 'new') {
      fetchBranch();
    }
  }, [id]);

  const fetchBranch = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getBranch(id);
      console.log('Branch API Response:', response);
      
      const branchData = response.branch || response.data || response;
      setBranch(branchData);

      const data = {
        branchName: branchData.branchName || '',
        address: branchData.address || '',
        city: branchData.city || '',
        state: branchData.state || '',
        zipCode: branchData.zipCode || '',
        phone: branchData.phone || '',
        email: branchData.email || '',
        contactPersonName: branchData.contactPersonName || '',
        description: branchData.description || '',
        type: branchData.type || '',
        // Include subscription fields
        subscriptionId: branchData.subscriptionId || null,
        subscriptionPlan: branchData.subscriptionPlan || null,
        subscriptionStatus: branchData.subscriptionStatus || null,
      };

      console.log('Parsed branch data with subscription:', data);

      setCompanyId(branchData.companyId || '');
      setFormData(data);
      setOriginalData(data);
    } catch (error) {
      console.error('Error fetching branch:', error);
      toast.error('Failed to fetch branch');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.branchName.trim()) {
      newErrors.branchName = 'Branch name is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
    setErrors({});
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setFormData(originalData);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix all validation errors');
      return;
    }

    setIsLoading(true);
    try {
      // Clean up the data - only include non-empty optional fields
      const submitData = {
        branchName: formData.branchName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
      };

      // Only add optional fields if they have values
      if (formData.contactPersonName.trim()) {
        submitData.contactPersonName = formData.contactPersonName;
      }
      if (formData.phone.trim()) {
        submitData.phone = formData.phone;
      }
      if (formData.email.trim()) {
        submitData.email = formData.email;
      }
      // Always include description and type to allow clearing them
      submitData.description = formData.description || '';
      submitData.type = formData.type || '';

      console.log('Submitting branch data:', submitData);
      await apiClient.updateBranch(id, submitData);
      
      setOriginalData(formData);
      toast.success('Branch updated successfully!');
      setIsDrawerOpen(false);
    } catch (error) {
      console.error('Error updating branch:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update branch';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewObituaries = () => {
    // Navigate to nested obituaries route if we have companyId
    const effectiveCompanyId = companyIdFromRoute || companyId;
    if (effectiveCompanyId) {
      navigate(`/companies/${effectiveCompanyId}/branches/${id}/obituaries`);
    } else {
      // Fallback to regular obituaries list if no companyId available
      navigate(`/obituaries?branchId=${id}`);
    }
  };

  // Show loading state within the content instead of replacing the entire page

  return (
    <DashboardLayout title="Branch Details">
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <LocationIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                {originalData.branchName || 'Branch'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage branch information
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<DescriptionIcon />}
              onClick={handleViewObituaries}
            >
              View Obituaries
            </Button>
          </Box>
        </Box>

        {/* Branch Information Display */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <Typography>Loading branch information...</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Branch Information
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleOpenDrawer}
                  >
                    Edit
                  </Button>
                </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Branch Name */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Branch Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {originalData.branchName || '-'}
                </Typography>
              </Box>

              <Divider />

              {/* Type and Description */}
              <Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                  {originalData.type && (
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Type
                      </Typography>
                      <Typography variant="body1">
                        {originalData.type}
                      </Typography>
                    </Box>
                  )}
                  {originalData.description && (
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {originalData.description}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {(originalData.type || originalData.description) && <Divider />}

              {/* Address Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Address
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {originalData.address || '-'}
                </Typography>
                <Typography variant="body1">
                  {originalData.city && originalData.state && originalData.zipCode
                    ? `${originalData.city}, ${originalData.state} ${originalData.zipCode}`
                    : '-'}
                </Typography>
              </Box>

              <Divider />

              {/* Contact Information Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Contact Information
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 3
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Contact Person
                    </Typography>
                    <Typography variant="body1">
                      {originalData.contactPersonName || '-'}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {originalData.phone || '-'}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {originalData.email || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              {/* Subscription Information Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <CreditCardIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Subscription
                  </Typography>
                </Box>
                
                {originalData.subscriptionPlan ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 3
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Plan Type
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {originalData.subscriptionPlan === 'monthly' ? 'Monthly' : 'Annual'}
                        </Typography>
                        <Box sx={{
                          backgroundColor: originalData.subscriptionStatus === 'active' ? '#10b981' : '#6b7280',
                          color: 'white',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase'
                        }}>
                          {originalData.subscriptionStatus || 'Unknown'}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Subscription ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {originalData.subscriptionId ? originalData.subscriptionId.substring(0, 20) + '...' : '-'}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Billing Amount
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {originalData.subscriptionPlan === 'monthly' ? '$25/month' : '$250/year'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: '#f9fafb', 
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      No subscription attached to this branch
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ 
                        mt: 2,
                        backgroundColor: '#2c4575',
                        '&:hover': { backgroundColor: '#1e3154' }
                      }}
                      onClick={() => navigate('/')}
                    >
                      Add Subscription
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit Drawer */}
        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          PaperProps={{
            sx: { 
              width: { xs: '100%', sm: 500 },
              backgroundColor: '#ffffff'
            }
          }}
        >
          <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {/* Drawer Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Edit Branch Information
              </Typography>
              <IconButton onClick={handleCloseDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Edit Form */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Branch Name */}
              <TextField
                fullWidth
                label="Branch Name"
                value={formData.branchName}
                onChange={(e) => handleInputChange('branchName', e.target.value)}
                error={!!errors.branchName}
                helperText={errors.branchName}
                required
              />

              {/* Type */}
              <TextField
                fullWidth
                label="Type"
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                placeholder="e.g., Main Office, Satellite Location"
              />

              {/* Description */}
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                placeholder="e.g., Celebrating Lives & Caring for Families"
              />

              {/* Address Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Address
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    error={!!errors.address}
                    helperText={errors.address}
                    required
                  />

                  <TextField
                    fullWidth
                    label="City"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    error={!!errors.city}
                    helperText={errors.city}
                    required
                  />

                  <TextField
                    fullWidth
                    label="State"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    error={!!errors.state}
                    helperText={errors.state}
                    required
                  />

                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    error={!!errors.zipCode}
                    helperText={errors.zipCode}
                    required
                  />
                </Box>
              </Box>

              {/* Contact Details Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Contact Details
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Contact Person Name"
                    value={formData.contactPersonName}
                    onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                  />

                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />

                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </Box>
              </Box>
            </Box>

            {/* Drawer Action Button */}
            <Box sx={{ mt: 4 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                startIcon={<SaveIcon />}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Drawer>
      </Box>
    </DashboardLayout>
  );
};

export default BranchDetailPage;
